import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Device } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useDevicesQuery } from '@/hooks/useTreatmentLibraryQueries';
import { useSetDeviceActiveMutation } from '@/hooks/useTreatmentLibraryMutations';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { DeviceFormModal } from './DeviceFormModal';
import { AssistantAskButton } from '@/components/assistant/AssistantAskButton';
import { AssistantTargetRegion } from '@/components/assistant/uiActions/AssistantTargetRegion';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { useAssistantTarget } from '@/components/assistant/uiActions/useAssistantTarget';
import { resolveOtherLanguageName } from './localizedName';

export function DevicesPane() {
  const { t } = useTranslation('treatmentLibrary');
  const devicesQuery = useDevicesQuery(true);
  const setActiveMutation = useSetDeviceActiveMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Device | null>(null);
  const addButtonTargetRef = useAssistantTarget(ASSISTANT_TARGET_KEY.devicesAddButton);

  function openAdd() {
    setEditing(null);
    setIsModalOpen(true);
  }
  function openEdit(device: Device) {
    setEditing(device);
    setIsModalOpen(true);
  }
  function toggleActive(device: Device) {
    setActiveMutation.mutate(
      { deviceId: device.id, isActive: !device.isActive },
      {
        onSuccess: function done() {
          showSuccessNotification(
            device.isActive ? t('devices.deactivated') : t('devices.activated')
          );
        },
        onError: function fail(error) {
          showErrorNotification(resolveApiErrorMessage(error));
        },
      }
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-xs text-zoomuted flex items-start gap-2">
          <Icon name="Info" className="shrink-0 mt-0.5" />
          {t('devices.note')}
        </p>
        <Button
          ref={addButtonTargetRef}
          type="button"
          className="flex-shrink-0 self-start sm:self-auto"
          onClick={openAdd}
        >
          <Icon name="Plus" /> {t('devices.add')}
        </Button>
      </div>

      {devicesQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : devicesQuery.isError === true || devicesQuery.data === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            devicesQuery.refetch();
          }}
        />
      ) : devicesQuery.data.length === 0 ? (
        <Card
          padding="lg"
          variant="dashed"
          shadow={false}
          className="text-center text-sm text-zoomuted"
        >
          {t('devices.empty')}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {devicesQuery.data.map(function renderCard(device) {
            return (
              <DeviceCard
                key={device.id}
                device={device}
                onEdit={openEdit}
                onToggleActive={toggleActive}
                isTogglingActive={
                  setActiveMutation.isPending &&
                  setActiveMutation.variables !== undefined &&
                  setActiveMutation.variables.deviceId === device.id
                }
              />
            );
          })}
        </div>
      )}

      {isModalOpen ? (
        <DeviceFormModal
          device={editing}
          onClose={function close() {
            setIsModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface DeviceCardProps {
  device: Device;
  onEdit: (device: Device) => void;
  onToggleActive: (device: Device) => void;
  isTogglingActive: boolean;
}

function DeviceCard({ device, onEdit, onToggleActive, isTogglingActive }: DeviceCardProps) {
  const { t, i18n } = useTranslation('treatmentLibrary');
  const { t: translateAssistant } = useTranslation('assistant');
  const editButtonTargetRef = useAssistantTarget(ASSISTANT_TARGET_KEY.deviceEditButton, device.id);
  const deviceName = i18n.language === 'pl' ? device.namePl : device.nameEn;
  return (
    <AssistantTargetRegion targetKey={ASSISTANT_TARGET_KEY.deviceCard} entityId={device.id}>
      <Card
        as="article"
        padding="none"
        className={
          device.isActive ? 'p-4 flex flex-col gap-3' : 'p-4 flex flex-col gap-3 opacity-60'
        }
      >
        <div className="flex items-start gap-3">
          {device.imageUrl === null ? (
            <span className="w-10 h-10 rounded-xl bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
              <Icon name="Monitor" />
            </span>
          ) : (
            <img
              src={device.imageUrl}
              alt={deviceName}
              className="w-10 h-10 rounded-xl object-cover border border-zooborder shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold text-zooindigo line-clamp-2 break-words">
              {deviceName}
            </div>
            <div className="text-xs text-zoomuted truncate">
              {resolveOtherLanguageName(device.nameEn, device.namePl)}
            </div>
          </div>
          <AssistantAskButton
            subject={deviceName}
            question={translateAssistant('askAbout.device', { name: deviceName })}
          />
        </div>

        <div className="space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
            {t('devices.recordsLabel')}
          </div>
          {device.settingFields.length === 0 ? (
            <p className="text-xs text-zoomuted">{t('devices.noFields')}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {device.settingFields.map(function renderField(field) {
                const suffix =
                  field.fieldType === 'choice'
                    ? ' · ' + t('devices.choiceCount', { count: field.options.length })
                    : field.unit.length > 0
                      ? ' (' + field.unit + ')'
                      : '';
                return (
                  <span
                    key={field.id === null ? field.name : field.id}
                    className="text-[11.5px] font-semibold text-zooindigo bg-zoooffwhite border border-zooborder rounded-lg px-2 py-0.5"
                  >
                    {field.name}
                    {suffix}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-zooborder pt-2.5 mt-0.5">
          <span className="text-xs text-zoomuted">
            {device.isActive
              ? t('devices.usedByTreatments', { count: device.treatmentCount })
              : t('devices.inactive')}
          </span>
          <div className="flex items-center gap-3">
            <Button
              ref={editButtonTargetRef}
              variant="ghost"
              size="icon"
              aria-label={t('devices.edit')}
              onClick={function edit() {
                onEdit(device);
              }}
            >
              <Icon name="Edit" />
            </Button>
            <ToggleSwitch
              isChecked={device.isActive}
              isPending={isTogglingActive}
              onToggle={function toggle() {
                onToggleActive(device);
              }}
              accessibleLabel={t('devices.activeLabel')}
            />
          </div>
        </div>
      </Card>
    </AssistantTargetRegion>
  );
}
